import { InfoCircleOutlined } from "@ant-design/icons";
import { Button, Col, Form, Input, Row, Select, Tooltip } from "antd";

const { TextArea } = Input;

const sustainedTooltipText = "Select Yes, if you believe that this annotation will persist for an extended period of time and may not be a single task. ";

const AddAnnotationForm = ({ users = [], fields = [], onSubmit, onCancel, submitText = "Add Annotation", placeholder = "Add an annotation...", isSubmitting = false }) => {
    const [form] = Form.useForm();
    const hasFieldOptions = Array.isArray(fields) && fields.some((f) => f && f.name);

    const onFinish = (values) => {
        if (values.content && values.content.trim()) {
            const annotationData = {
                content: values.content.trim(),
                type: values.type,
                ...(values.state && { state: values.state }),
                ...(values.priority && { priority: values.priority }),
                ...(values.assignedTo && { assignedTo: values.assignedTo }),
                ...(values.targetName && { targetName: values.targetName }),
                ...(values.sustained !== undefined && { sustained: values.sustained }),
            };
            onSubmit(annotationData);
            form.resetFields();
        }
    };

    return (
        <Form form={form} onFinish={onFinish} layout="vertical" style={{ marginBottom: "1rem", paddingTop: 8 }}>
            <Form.Item name="type" label="Type" rules={[{ required: true, message: "Please select annotation type." }]} style={{ marginBottom: 12 }}>
                <Select placeholder="Select type" allowClear>
                    <Select.Option value="issue">Issue</Select.Option>
                    <Select.Option value="review">Review</Select.Option>
                    <Select.Option value="info">Info</Select.Option>
                </Select>
            </Form.Item>

            <Form.Item name="content" label="Content" rules={[{ required: true, message: "Content cannot be empty." }]} style={{ marginBottom: "12px" }}>
                <TextArea rows={3} placeholder={placeholder} allowClear />
            </Form.Item>

            <Row gutter={12} style={{ marginBottom: 12 }}>
                <Col xs={24} md={8}>
                    <Form.Item name="state" label="State" style={{ marginBottom: 0 }}>
                        <Select placeholder="State" allowClear>
                            <Select.Option value="open">Open</Select.Option>
                            <Select.Option value="in_progress">In Progress</Select.Option>
                            <Select.Option value="resolved">Resolved</Select.Option>
                            <Select.Option value="dismissed">Dismissed</Select.Option>
                        </Select>
                    </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                    <Form.Item name="priority" label="Priority" style={{ marginBottom: 0 }}>
                        <Select placeholder="Priority" allowClear>
                            <Select.Option value="low">Low</Select.Option>
                            <Select.Option value="medium">Medium</Select.Option>
                            <Select.Option value="high">High</Select.Option>
                        </Select>
                    </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                    <Form.Item
                        name="sustained"
                        label={
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                Sustained
                                <Tooltip title={sustainedTooltipText} placement="top">
                                    <InfoCircleOutlined style={{ color: "rgba(0, 0, 0, 0.45)" }} />
                                </Tooltip>
                            </span>
                        }
                        style={{ marginBottom: 0 }}
                    >
                        <Select placeholder="Sustained?" allowClear>
                            <Select.Option value={true}>Yes</Select.Option>
                            <Select.Option value={false}>No</Select.Option>
                        </Select>
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={12} style={{ marginBottom: 12 }}>
                <Col xs={24} md={12}>
                    <Form.Item name="assignedTo" label="Assign To" style={{ marginBottom: 0 }}>
                        <Select placeholder="User" allowClear showSearch optionFilterProp="children">
                            {users
                                .filter((user) => user.active && !user.deleted)
                                .map((user) => (
                                    <Select.Option key={user.userId} value={user.userId}>
                                        {`${user.firstName} ${user.lastName}`}
                                    </Select.Option>
                                ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item name="targetName" label="Target Field" style={{ marginBottom: 0 }}>
                        {hasFieldOptions ? (
                            <Select placeholder="Field" allowClear showSearch optionFilterProp="children">
                                {fields
                                    .filter((f) => f && f.name)
                                    .map((f) => (
                                        <Select.Option key={f.fieldId || f.name} value={f.name}>
                                            {f.name}
                                        </Select.Option>
                                    ))}
                            </Select>
                        ) : (
                            <Input placeholder="Target field" allowClear />
                        )}
                    </Form.Item>
                </Col>
            </Row>

            <Row justify="end" gutter={8} style={{ marginTop: 8 }}>
                {onCancel && (
                    <Col>
                        <Button onClick={onCancel}>Cancel</Button>
                    </Col>
                )}
                <Col>
                    <Button htmlType="submit" type="primary" loading={isSubmitting} disabled={isSubmitting}>
                        {isSubmitting ? "Adding Annotation" : submitText}
                    </Button>
                </Col>
            </Row>
        </Form>
    );
};

export default AddAnnotationForm;
